import type {Values} from '@wix/devzai-utils-common';
import {DoppeProductsGridLayoutSize} from '../doppe-products-grid-layout-size';
import {DoppeActionLayoutMediaRatio} from '../doppe-action-layout';
import {DoppeRowTemplateButtonDesign} from '../doppe-row-template-button-design/doppe-row-template-button-design';

/**
 * Predefined template IDs for event list pages.
 * Each template defines a different visual layout and behavior.
 */
export const DoppeEventListPageTemplateId = {
    /**
     * Frameless template - clean grid layout without card borders.
     * Image and text displayed without container borders.
     * Uses DoppeProductsGridLayoutSize to control item size.
     */
    FramelessTemplate: 'FramelessTemplate',

    /**
     * Card template with bordered card containers.
     * Each event is displayed in a card with subtle borders.
     * Uses DoppeProductsGridLayoutSize to control item size.
     */
    CardTemplate: 'CardTemplate',

    /**
     * On-image template with text overlay on the event image.
     * Event info is displayed on top of the image.
     * Uses DoppeProductsGridLayoutSize to control item size.
     * Fixed image ratio (not editable).
     */
    OnImageTemplate: 'OnImageTemplate',

    /**
     * Row template with horizontal list layout.
     * Each event is displayed as a full-width row.
     * Uses flex column layout (not grid).
     * Does not support layout size or custom image ratio.
     */
    RowTemplate: 'RowTemplate',
} as const;

export type DoppeEventListPageTemplateId = Values<typeof DoppeEventListPageTemplateId>;

/**
 * Template kind for event list pages.
 */
export const DoppeEventListPageTemplateKind = {
    /**
     * A predefined template with a specific templateId.
     */
    Predefined: 'predefined',
} as const;

export type DoppeEventListPageTemplateKind = Values<typeof DoppeEventListPageTemplateKind>;

export const DoppeEventsImageRatio = DoppeActionLayoutMediaRatio;

export type DoppeEventsImageRatio = Values<typeof DoppeEventsImageRatio>;

/**
 * Template selector for event list pages.
 * Specifies which template to use for rendering the page.
 */
export type DoppeEventListPageTemplate = DoppeEventListPageTemplate.Predefined;

export namespace DoppeEventListPageTemplate {
    /**
     * A predefined template with a specific templateId.
     */
    export interface Predefined {
        kind: typeof DoppeEventListPageTemplateKind.Predefined;
        templateId: DoppeEventListPageTemplateId;
    }
}

/**
 * Template-specific props for each event list page template.
 * Each template has its own set of configurable properties.
 */
export type DoppeEventListPageTemplateProps = 
    | DoppeEventListPageTemplateProps.FramelessTemplate
    | DoppeEventListPageTemplateProps.CardTemplate
    | DoppeEventListPageTemplateProps.OnImageTemplate
    | DoppeEventListPageTemplateProps.RowTemplate
    // Legacy types for backward compatibility
    | DoppeEventListPageTemplateProps.GridTemplate
    | DoppeEventListPageTemplateProps.ListTemplate;

export namespace DoppeEventListPageTemplateProps {
    /**
     * Props for the FramelessTemplate.
     * Clean grid layout without card borders.
     * Uses responsive grid with min/max widths based on layout size.
     */
    export interface FramelessTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeEventListPageTemplateId.FramelessTemplate;

        /**
         * Layout size for the events grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;

        /**
         * Image aspect ratio for event images.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the CardTemplate.
     * Grid layout with bordered card containers.
     * Uses responsive grid with min/max widths based on layout size.
     */
    export interface CardTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeEventListPageTemplateId.CardTemplate;

        /**
         * Layout size for the events grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;

        /**
         * Image aspect ratio for event images.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the OnImageTemplate.
     * Grid layout with text overlay on images.
     * Uses responsive grid with min/max widths based on layout size.
     * For Small layout size: only vertical ratios (1:1, 4:5, 3:4, 2:3, 9:16).
     * For Medium layout size: full range of ratios available.
     */
    export interface OnImageTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeEventListPageTemplateId.OnImageTemplate;

        /**
         * Layout size for the events grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;
        
        /**
         * Image aspect ratio for event images.
         * For Small layout size: only vertical ratios are available (1:1, 4:5, 3:4, 2:3, 9:16).
         * For Medium layout size: full range of ratios available.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the RowTemplate.
     * Flex column layout with horizontal rows.
     * Does not support layout size or custom image ratio.
     */
    export interface RowTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeEventListPageTemplateId.RowTemplate;
        
        /**
         * Whether to show the media/image in the row template.
         * When false, the image container is hidden and only text content is shown.
         */
        showMedia: boolean;
        
        // Note: No layoutSize - uses flex column layout
        // Note: No imageRatio - uses fixed ratio
    }

    // Legacy interfaces for backward compatibility
    /**
     * @deprecated Use FramelessTemplate instead
     */
    export interface GridTemplate {
        layoutSize: DoppeProductsGridLayoutSize;
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * @deprecated Use RowTemplate instead
     */
    export interface ListTemplate {
        layoutSize: DoppeProductsGridLayoutSize;
        imageRatio: DoppeActionLayoutMediaRatio;
    }
}

/**
 * Common props shared by all event list page templates.
 * These props are template-agnostic and control shared behavior.
 */
export interface DoppeEventListPageCommonProps {
    /**
     * Whether to show the event description.
     * @default true
     */
    showDescription: boolean;

    /**
     * Whether to show the event price.
     * @default true
     */
    showPrice: boolean;

    /**
     * Whether to show the event date/time.
     * @default true
     */
    showDate: boolean;

    /**
     * Whether to show the event location.
     * @default true
     */
    showLocation: boolean;

    /**
     * Whether to show the event type badge (e.g., "Live Performance").
     * @default true
     */
    showTypeBadge: boolean;

    /**
     * Whether to show the Learn More button on event cards.
     * Only applicable to RowTemplate - other templates don't show action buttons.
     * @default false
     */
    showLearnMoreButton: boolean;

    /**
     * Custom text for the Learn More button.
     * Only applicable to RowTemplate.
     * @default "Learn More"
     */
    learnMoreButtonText: string;

    /**
     * The visual design of the action button in row template.
     * - Icon: Textual link with arrow icon
     * - IconButton: Circular icon button
     * - Button: Full text button
     * - None: No button displayed
     * Only applicable to RowTemplate.
     * @default "button"
     */
    buttonDesign?: DoppeRowTemplateButtonDesign;

    /**
     * Whether to show past (ended) events in addition to upcoming events.
     * When true, both upcoming and past events are shown.
     * When false, only upcoming (scheduled/started) events are shown.
     * @default true
     */
    showPastEvents: boolean;

    /**
     * Whether to show the horizontal categories menu above the list.
     * @default true
     */
    showCategoriesMenu: boolean;
}

/**
 * Creates default template settings for an event list page.
 * @param templateId - Optional template ID. Uses FramelessTemplate if not specified.
 */
export function doppeEventListPageTemplateCreateDefault(
    templateId: DoppeEventListPageTemplateId = DoppeEventListPageTemplateId.FramelessTemplate
): DoppeEventListPageTemplate {
    return {
        kind: DoppeEventListPageTemplateKind.Predefined,
        templateId: templateId,
    };
}

/**
 * Creates default template props for a given template.
 */
export function doppeEventListPageTemplatePropsCreateDefault(
    templateId: DoppeEventListPageTemplateId
): DoppeEventListPageTemplateProps {
    switch (templateId) {
        case DoppeEventListPageTemplateId.FramelessTemplate:
            return {
                templateType: DoppeEventListPageTemplateId.FramelessTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Ratio16By9,
            };
        case DoppeEventListPageTemplateId.CardTemplate:
            return {
                templateType: DoppeEventListPageTemplateId.CardTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Ratio16By9,
            };
        case DoppeEventListPageTemplateId.OnImageTemplate:
            return {
                templateType: DoppeEventListPageTemplateId.OnImageTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Square,
            };
        case DoppeEventListPageTemplateId.RowTemplate:
            return {
                templateType: DoppeEventListPageTemplateId.RowTemplate,
                showMedia: true,
            };
    }
}

/**
 * Creates default common props for an event list page.
 */
export function doppeEventListPageCommonPropsCreateDefault(): DoppeEventListPageCommonProps {
    return {
        showDescription: true,
        showPrice: true,
        showDate: true,
        showLocation: true,
        showTypeBadge: true,
        showLearnMoreButton: false,
        learnMoreButtonText: 'See more',
        buttonDesign: DoppeRowTemplateButtonDesign.Button,
        showPastEvents: true,
        showCategoriesMenu: false,
    };
}

/**
 * Type guard to check if template props are for FramelessTemplate.
 */
export function doppeEventListPageTemplatePropsIsFramelessTemplate(
    props: DoppeEventListPageTemplateProps
): props is DoppeEventListPageTemplateProps.FramelessTemplate {
    return 'templateType' in props && props.templateType === DoppeEventListPageTemplateId.FramelessTemplate;
}

/**
 * Type guard to check if template props are for CardTemplate.
 */
export function doppeEventListPageTemplatePropsIsCardTemplate(
    props: DoppeEventListPageTemplateProps
): props is DoppeEventListPageTemplateProps.CardTemplate {
    return 'templateType' in props && props.templateType === DoppeEventListPageTemplateId.CardTemplate;
}

/**
 * Type guard to check if template props are for OnImageTemplate.
 */
export function doppeEventListPageTemplatePropsIsOnImageTemplate(
    props: DoppeEventListPageTemplateProps
): props is DoppeEventListPageTemplateProps.OnImageTemplate {
    return 'templateType' in props && props.templateType === DoppeEventListPageTemplateId.OnImageTemplate;
}

/**
 * Type guard to check if template props are for RowTemplate.
 */
export function doppeEventListPageTemplatePropsIsRowTemplate(
    props: DoppeEventListPageTemplateProps
): props is DoppeEventListPageTemplateProps.RowTemplate {
    return 'templateType' in props && props.templateType === DoppeEventListPageTemplateId.RowTemplate;
}

/**
 * Type guard to check if template props support layout size configuration.
 * FramelessTemplate, CardTemplate, and OnImageTemplate support layout size.
 * RowTemplate does not support layout size.
 */
export function doppeEventListPageTemplatePropsSupportsLayoutSize(
    props: DoppeEventListPageTemplateProps
): props is DoppeEventListPageTemplateProps.FramelessTemplate | DoppeEventListPageTemplateProps.CardTemplate | DoppeEventListPageTemplateProps.OnImageTemplate {
    return 'layoutSize' in props;
}

/**
 * Type guard to check if template props support image ratio configuration.
 * FramelessTemplate, CardTemplate, and OnImageTemplate support image ratio.
 * RowTemplate has fixed ratio.
 */
export function doppeEventListPageTemplatePropsSupportsImageRatio(
    props: DoppeEventListPageTemplateProps
): props is DoppeEventListPageTemplateProps.FramelessTemplate | DoppeEventListPageTemplateProps.CardTemplate | DoppeEventListPageTemplateProps.OnImageTemplate {
    return 'imageRatio' in props && 'layoutSize' in props;
}

/**
 * Type guard to check if template uses grid layout.
 * FramelessTemplate, CardTemplate, and OnImageTemplate use grid layout.
 * RowTemplate uses flex column layout.
 */
export function doppeEventListPageTemplatePropsUsesGridLayout(
    props: DoppeEventListPageTemplateProps
): boolean {
    if (!('templateType' in props)) {
        // Legacy props - check for legacy GridTemplate or CardTemplate
        return 'layoutSize' in props;
    }
    return props.templateType !== DoppeEventListPageTemplateId.RowTemplate;
}

/**
 * @deprecated Use doppeEventListPageTemplatePropsIsFramelessTemplate instead
 * Type guard to check if template props are for GridTemplate (legacy).
 */
export function doppeEventListPageTemplatePropsIsGridTemplate(
    props: DoppeEventListPageTemplateProps
): props is DoppeEventListPageTemplateProps.GridTemplate {
    return 'layoutSize' in props && !('templateType' in props);
}

